import { MAX_CONTACT_PAIRS, MAX_DROPS, MAX_PARTICLES, MAX_SLIMES } from '../core/constants';
import { SlimeState, type ContentCatalog } from '../content/types';

/** Shared by gameplay stores; never reset when slots or levels are cleared. */
export class RecordingEntityIds {
  private nextId = 1;

  allocate(): number {
    if (this.nextId > 0xffffffff) throw new Error('Recording entity ID capacity exceeded');
    return this.nextId++;
  }
}

/** O(1) 槽位分配。首次分配从 0 开始升序，回收后最近释放优先。
 *  调用方必须保证同一槽位不被重复 give——重复入栈会让两个实体共用一个槽位。 */
class SlotAllocator {
  private readonly stack: Int32Array;
  private top = 0;

  constructor(private readonly capacity: number) { this.stack = new Int32Array(capacity); this.reset(); }

  /** 栈底放大号、栈顶放 0，于是首次分配按 0、1、2…… 升序弹出。 */
  reset(): void {
    for (let index = 0; index < this.capacity; index += 1) this.stack[index] = this.capacity - 1 - index;
    this.top = this.capacity;
  }

  /** -1 表示容量耗尽；是否视为错误由各 Store 决定。 */
  take(): number { return this.top === 0 ? -1 : this.stack[this.top -= 1]; }
  give(id: number): void { this.stack[this.top] = id; this.top += 1; }
}

export class SlimeStore {
  readonly capacity = MAX_SLIMES;
  readonly active = new Uint8Array(this.capacity);
  readonly recordingEntityId = new Uint32Array(this.capacity);
  readonly generation = new Uint16Array(this.capacity);
  readonly x = new Float32Array(this.capacity);
  readonly y = new Float32Array(this.capacity);
  readonly vx = new Float32Array(this.capacity);
  readonly vy = new Float32Array(this.capacity);
  readonly radius = new Float32Array(this.capacity);
  readonly scaleX = new Float32Array(this.capacity);
  readonly scaleY = new Float32Array(this.capacity);
  readonly scaleVelocity = new Float32Array(this.capacity);
  readonly definition = new Uint8Array(this.capacity);
  readonly flags = new Uint16Array(this.capacity);
  readonly seed = new Float32Array(this.capacity);
  /** 每只的落速倍率，乘在重力上。spawn 时为 1；波次投放（T048）按波次写入，种类（T049）按定义写入。
   *  落速做在史莱姆身上而非环境上：它是最强的难度旋钮，也是「重砣」的预演。 */
  readonly fallScale = new Float32Array(this.capacity);
  /** 本固定步累计的接触深度：与邻居重叠多少、贴墙多深。纯几何量，每步开始清零。
   *  由 PhysicsSystem 的分离循环顺手输出（那里本就在算 overlap），mechanics 解释为保压。 */
  readonly contactDepth = new Float32Array(this.capacity);
  /** 本固定步发生重叠的槽位对，与 contactDepth 同源、同一循环顺手输出。纯几何事实，
   *  谁与谁碰到了；mechanics 解释为黏连。每步开始清零，满了丢弃并计数而不抛错——漏记的一对
   *  下一步仍在重叠，会被重新记到。 */
  readonly contactA = new Int16Array(MAX_CONTACT_PAIRS);
  readonly contactB = new Int16Array(MAX_CONTACT_PAIRS);
  contactCount = 0;
  contactDropped = 0;
  activeCount = 0;
  private readonly slots = new SlotAllocator(this.capacity);
  /** 当前活动史莱姆的最大半径。半径随体积动态变化，邻域查询半径必须按它计算，
   *  不能写死常量。由 SpatialGridSystem 在每次重建网格时顺带刷新。 */
  maxActiveRadius = 0;

  constructor(private readonly catalog: ContentCatalog, private readonly recordingIds: RecordingEntityIds) {}

  spawn(x: number, y: number, type: number): number {
    const definition = this.catalog.slimes[type];
    if (definition === undefined) throw new Error(`Unknown compiled slime type ${type}`);
    const id = this.slots.take();
    if (id < 0) throw new Error('SlimeStore capacity exceeded');
    this.recordingEntityId[id] = this.recordingIds.allocate();
    this.active[id] = 1;
    this.generation[id] += 1;
    this.x[id] = x; this.y[id] = y; this.vx[id] = 0; this.vy[id] = 0;
    this.radius[id] = definition.radiusRest; this.scaleX[id] = 1; this.scaleY[id] = 1; this.scaleVelocity[id] = 0;
    this.definition[id] = type; this.flags[id] = definition.behaviors;
    this.seed[id] = ((id * 29) % 97) / 97; this.fallScale[id] = 1;
    this.activeCount += 1;
    return id;
  }

  release(id: number): void {
    if (!Number.isInteger(id) || id < 0 || id >= this.capacity || this.active[id] === 0) return;
    this.active[id] = 0; this.flags[id] = 0; this.activeCount -= 1; this.slots.give(id);
  }

  clear(): void { this.active.fill(0); this.flags.fill(0); this.activeCount = 0; this.contactCount = 0; this.slots.reset(); }
  recordContact(a: number, b: number): void {
    if (this.contactCount >= MAX_CONTACT_PAIRS) { this.contactDropped += 1; return; }
    this.contactA[this.contactCount] = a; this.contactB[this.contactCount] = b; this.contactCount += 1;
  }
  isDying(id: number): boolean { return (this.flags[id] & SlimeState.MarkedDead) !== 0; }
}

export class DropStore {
  readonly capacity = MAX_DROPS;
  readonly active = new Uint8Array(this.capacity);
  readonly recordingEntityId = new Uint32Array(this.capacity);
  readonly x = new Float32Array(this.capacity);
  readonly y = new Float32Array(this.capacity);
  readonly previousX = new Float32Array(this.capacity);
  readonly previousY = new Float32Array(this.capacity);
  readonly vx = new Float32Array(this.capacity);
  readonly vy = new Float32Array(this.capacity);
  readonly radius = new Float32Array(this.capacity);
  readonly definition = new Uint8Array(this.capacity);
  activeCount = 0;
  private readonly slots = new SlotAllocator(this.capacity);

  constructor(private readonly catalog: ContentCatalog, private readonly recordingIds: RecordingEntityIds) {}
  spawn(x: number, y: number, vx: number, vy: number, type: number): number {
    const definition = this.catalog.drops[type];
    if (definition === undefined) throw new Error(`Unknown compiled drop type ${type}`);
    const id = this.slots.take();
    if (id < 0) throw new Error('DropStore capacity exceeded');
    this.recordingEntityId[id] = this.recordingIds.allocate();
    this.active[id] = 1; this.x[id] = x; this.y[id] = y; this.previousX[id] = x; this.previousY[id] = y;
    this.vx[id] = vx; this.vy[id] = vy; this.radius[id] = definition.radius; this.definition[id] = type;
    this.activeCount += 1; return id;
  }
  release(id: number): void { if (Number.isInteger(id) && id >= 0 && id < this.capacity && this.active[id] !== 0) { this.active[id] = 0; this.activeCount -= 1; this.slots.give(id); } }
  clear(): void { this.active.fill(0); this.activeCount = 0; this.slots.reset(); }
}

export class ParticleStore {
  readonly capacity = MAX_PARTICLES;
  readonly active = new Uint8Array(this.capacity);
  readonly x = new Float32Array(this.capacity); readonly y = new Float32Array(this.capacity);
  readonly vx = new Float32Array(this.capacity); readonly vy = new Float32Array(this.capacity);
  readonly life = new Float32Array(this.capacity); readonly maxLife = new Float32Array(this.capacity);
  readonly size = new Float32Array(this.capacity); readonly color = new Uint32Array(this.capacity);
  activeCount = 0;
  private readonly slots = new SlotAllocator(this.capacity);

  /** 粒子是纯表现层，满了就丢弃并返回 -1——绝不抛错。调用方把 -1 当正常结果处理。 */
  spawn(x: number, y: number, vx: number, vy: number, life: number, size: number, color: number): number {
    const id = this.slots.take();
    if (id < 0) return -1;
    this.active[id] = 1; this.x[id] = x; this.y[id] = y; this.vx[id] = vx; this.vy[id] = vy;
    this.life[id] = life; this.maxLife[id] = life; this.size[id] = size; this.color[id] = color;
    this.activeCount += 1; return id;
  }
  release(id: number): void { if (Number.isInteger(id) && id >= 0 && id < this.capacity && this.active[id] !== 0) { this.active[id] = 0; this.activeCount -= 1; this.slots.give(id); } }
  clear(): void { this.active.fill(0); this.activeCount = 0; this.slots.reset(); }
}
